/**
 * REST-backed live search results (SEARCH-008).
 *
 * @package DemocraticTools
 */

const DEFAULT_MESSAGES = {
  loading: 'Loading search results…',
  error: 'Search is temporarily unavailable.',
  empty: 'No articles matched your search.',
  degraded: 'Showing simplified search results.',
};

/**
 * @param {string} baseUrl
 * @param {Record<string, string>} params
 * @returns {string}
 */
export function buildSearchUrl(baseUrl, params) {
  const origin = globalThis.location?.origin ?? 'https://example.test';
  const url = new URL(baseUrl, origin);
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === 'string' && value !== '') {
      url.searchParams.set(key, value);
    }
  }
  return url.toString();
}

/**
 * @param {Record<string, string>} params
 * @returns {Record<string, string>}
 */
export function restParamsFromForm(params) {
  return {
    q: params.s ?? '',
    role: params.role ?? '',
    market: params.market ?? '',
    topic: params.topic ?? '',
    format: params.format ?? '',
    date_from: params.date_from ?? '',
    date_to: params.date_to ?? '',
  };
}

/**
 * @param {HTMLFormElement} form
 * @returns {Record<string, string>}
 */
export function readFormParams(form) {
  /** @type {Record<string, string>} */
  const params = {};
  const elements = form.querySelectorAll('input, select, textarea');

  for (const element of elements) {
    if (!isFieldControl(element)) {
      continue;
    }

    const name = element.name;
    if (!name || element.type === 'submit' || element.type === 'file') {
      continue;
    }

    params[name] = element.value;
  }

  return params;
}

/**
 * @param {unknown} payload
 * @param {Record<string, string>} [messages]
 * @returns {string}
 */
export function renderRestResults(payload, messages = DEFAULT_MESSAGES) {
  if (!payload || typeof payload !== 'object') {
    return `<p class="dt-search-rest-results__message">${messages.error}</p>`;
  }

  const items = Array.isArray(payload.items) ? payload.items : [];
  if (items.length === 0) {
    return `<p class="dt-search-rest-results__message">${messages.empty}</p>`;
  }

  const degraded = payload.degraded === true
    ? `<p class="dt-search-rest-results__message">${messages.degraded}</p>`
    : '';

  const list = items.map((item) => {
    if (!item || typeof item !== 'object') {
      return '';
    }
    const title = typeof item.title === 'string' ? item.title : '';
    const url = typeof item.permalink === 'string' ? item.permalink : '#';
    const excerpt = typeof item.excerpt === 'string' && item.excerpt !== ''
      ? item.excerpt
      : (typeof item.standfirst === 'string' ? item.standfirst : '');
    return `<li class="dt-search-rest-results__item"><h3><a href="${url}">${title}</a></h3><p>${excerpt}</p></li>`;
  }).join('');

  return `${degraded}<ul class="dt-search-rest-results__list">${list}</ul>`;
}

/**
 * @param {object} [config]
 */
export async function initSearch(config) {
  const resolved = config ?? globalThis.dtSearchConfig ?? {};
  const root = document.querySelector(resolved.selectors?.root ?? '.dt-search');
  if (!isElement(root)) {
    return;
  }

  const form = root.querySelector(resolved.selectors?.form ?? '.dt-search-facets__form');
  const live = root.querySelector(resolved.selectors?.live ?? '#dt-search-rest-results');
  if (!isFormElement(form) || !isElement(live)) {
    return;
  }

  const messages = { ...DEFAULT_MESSAGES, ...(resolved.messages ?? {}) };
  const restUrl = typeof resolved.restUrl === 'string' ? resolved.restUrl : '';

  const load = async () => {
    if (restUrl === '') {
      return;
    }

    live.textContent = messages.loading;
    const params = restParamsFromForm(readFormParams(form));

    try {
      const response = await fetch(buildSearchUrl(restUrl, params), {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        live.innerHTML = `<p class="dt-search-rest-results__message">${messages.error}</p>`;
        return;
      }
      const payload = await response.json();
      live.innerHTML = renderRestResults(payload, messages);
    } catch {
      live.innerHTML = `<p class="dt-search-rest-results__message">${messages.error}</p>`;
    }
  };

  form.addEventListener('submit', () => {
    globalThis.setTimeout(load, 0);
  });

  if (readFormParams(form).s !== '') {
    await load();
  }
}

/**
 * @param {unknown} value
 * @returns {value is HTMLElement}
 */
function isElement(value) {
  return typeof value === 'object' && value !== null && 'tagName' in value && 'classList' in value;
}

/**
 * @param {unknown} value
 * @returns {value is HTMLFormElement}
 */
function isFormElement(value) {
  return isElement(value) && value.tagName === 'FORM';
}

/**
 * @param {unknown} value
 * @returns {value is HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement}
 */
function isFieldControl(value) {
  if (!isElement(value)) {
    return false;
  }

  const tag = value.tagName;
  return tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA';
}
